import { Suspense, lazy } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, CardContent, Button } from '@agms/ui'
import { useLead } from '../hooks/useLeads'
import { useApplication, useCreateApplication } from '../hooks/useApplication'

// Lazy load the wizard component for code splitting
const TurboAppWizard = lazy(() => import('../components/TurboAppWizard'))

/**
 * TurboApp page - entry point for the application wizard.
 *
 * Handles:
 * - Loading the lead
 * - Creating application if one doesn't exist
 * - Rendering the wizard
 *
 * @see CLAUDE.md Section 7 - TurboApp Field Mapping
 */
export function TurboAppPage() {
  const { id: leadId } = useParams<{ id: string }>()

  const { data: lead, isLoading: leadLoading, error: leadError } = useLead(leadId)
  const { data: application, isLoading: appLoading, error: appError } = useApplication(leadId)
  const createApplication = useCreateApplication()

  // Loading state
  if (leadLoading || appLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading application...</div>
      </div>
    )
  }

  // Lead not found
  if (leadError || !lead) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">Lead not found or access denied.</div>
            <Link to="/leads">
              <Button variant="secondary">Back to Leads</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // No application yet - show create prompt
  if (appError || !application) {
    const handleCreateApplication = () => {
      createApplication.mutate(leadId!, {
        onSuccess: () => {
          // Application created, will be loaded automatically
        },
      })
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={`/leads/${leadId}`} className="text-gray-500 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">TurboApp</h1>
            <p className="mt-1 text-sm text-gray-500">
              {lead.dba_name || lead.legal_name || 'Unnamed Lead'}
            </p>
          </div>
        </div>

        {/* Create Application Prompt */}
        <Card>
          <CardContent className="py-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              No Application Started
            </h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Start a TurboApp application to begin the merchant onboarding process.
              This copies the lead into the wizard. When a statement analysis is selected,
              its proposed rates are copied into pricing and can still be edited.
            </p>
            <Button
              onClick={handleCreateApplication}
              isLoading={createApplication.isPending}
            >
              Start Application
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Application exists - render wizard
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading wizard...</div>
        </div>
      }
    >
      <TurboAppWizard lead={lead} application={application} />
    </Suspense>
  )
}

export default TurboAppPage
